import type { Editor } from "@tiptap/react";
import { useCallback, type ReactNode } from "react";

interface RichTextToolbarProps {
  editor: Editor | null;
}

interface ToolbarButtonProps {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  label: string;
  children: ReactNode;
}

const ToolbarButton = ({
  onClick,
  active,
  disabled,
  label,
  children,
}: ToolbarButtonProps) => (
  <button
    type="button"
    // Keep focus in the editor so commands apply to the current selection.
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    aria-pressed={active}
    title={label}
    className={`wysiwyg-toolbar__btn${active ? " is-active" : ""}`}
  >
    {children}
  </button>
);

/**
 * Formatting toolbar for {@link RichTextEditor}. Exposes the same set of formats
 * the previous Quill "snow" toolbar offered: headings, bold/italic/underline/
 * strike, ordered + bullet lists, link and clear-formatting.
 */
export const RichTextToolbar = ({ editor }: RichTextToolbarProps) => {
  const setLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Link URL", previous ?? "");
    // Cancelled prompt: leave the document untouched.
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div
      className="wysiwyg-toolbar"
      role="toolbar"
      aria-label="Text formatting"
    >
      <select
        className="wysiwyg-toolbar__select"
        aria-label="Heading level"
        // Kept out of the tab order (EWM-1012): as the first control before the
        // editable content, a focus trap falling back to "first tabbable
        // element" would otherwise land here, and a native <select> treats the
        // next keystroke as a type-ahead search rather than editor input.
        tabIndex={-1}
        value={
          editor.isActive("heading", { level: 1 })
            ? "1"
            : editor.isActive("heading", { level: 2 })
              ? "2"
              : editor.isActive("heading", { level: 3 })
                ? "3"
                : "p"
        }
        onChange={(e) => {
          const v = e.target.value;
          if (v === "p") {
            editor.chain().focus().setParagraph().run();
          } else {
            editor
              .chain()
              .focus()
              .toggleHeading({ level: Number(v) as 1 | 2 | 3 })
              .run();
          }
        }}
      >
        <option value="p">Normal</option>
        <option value="1">Heading 1</option>
        <option value="2">Heading 2</option>
        <option value="3">Heading 3</option>
      </select>

      <ToolbarButton
        label="Bold"
        active={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <span className="font-bold">B</span>
      </ToolbarButton>
      <ToolbarButton
        label="Italic"
        active={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <span className="italic">I</span>
      </ToolbarButton>
      <ToolbarButton
        label="Underline"
        active={editor.isActive("underline")}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <span className="underline">U</span>
      </ToolbarButton>
      <ToolbarButton
        label="Strikethrough"
        active={editor.isActive("strike")}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <span className="line-through">S</span>
      </ToolbarButton>

      <ToolbarButton
        label="Ordered list"
        active={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        1.
      </ToolbarButton>
      <ToolbarButton
        label="Bullet list"
        active={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        •
      </ToolbarButton>

      <ToolbarButton
        label="Link"
        active={editor.isActive("link")}
        onClick={setLink}
      >
        🔗
      </ToolbarButton>

      <ToolbarButton
        label="Clear formatting"
        onClick={() =>
          editor.chain().focus().unsetAllMarks().clearNodes().run()
        }
      >
        ⌫
      </ToolbarButton>
    </div>
  );
};
